import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { reassignTask } from "../../services/newdaily";
import styles from "./ReassignModal.module.css";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import ReassignModalIcon from "../../assets/svgs/reassignModalIcon.svg";
import SearchIcon from "../../assets/svgs/search.svg";
import CloseIcon from "../../assets/svgs/close.svg";
import UserCard from "../../components/UserCard/UserCard";

const ReassignModal = ({
    isOpen,
    onClose,
    task,
    allUsers = [],
    allTasks = [],
    selectedDate,
    isMobile,
    onSuccess,
    empleado,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedReassignUserId, setSelectedReassignUserId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        console.log("🧩 TASK RECIBIDA EN REASSIGN MODAL →", task);
        console.log("🧩 task.is_projection →", task?.is_projection);
        console.log("🧩 task.Fecha / task.date →", task?.Fecha, task?.date);
    }, [isOpen, task]);

    // Reset al abrir/cerrar
    useEffect(() => {
        if (!isOpen) return;
        setSearchTerm("");
        setSelectedReassignUserId(null);
    }, [isOpen]);

    // Bloquear scroll del body
    useEffect(() => {
        if (!isOpen) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    const durationMinutes = task?.Minutos ?? 0;
    const durationHours = Math.floor(durationMinutes / 60);
    const durationText = durationHours
        ? `${durationHours}h ${durationMinutes % 60 ? `${durationMinutes % 60}min` : ""}`
        : `${durationMinutes} min`;

    const startTime = task?.Inicio ? moment(task.Inicio, "HH:mm:ss") : null;
    const endTime = startTime ? moment(startTime).add(durationMinutes, "minutes") : null;
    const timeRange = startTime ? `${startTime.format("HH:mm")} - ${endTime.format("HH:mm")}` : "";

    const owner = useMemo(() => {
        return allTasks.find(item => item.tasks.some(t => t.id === task?.id))?.user;
    }, [allTasks, task?.id]);

    const filteredSortedUsers = useMemo(() => {
        const normalize = (str) =>
            (str || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase();

        return [...allUsers]
            .filter(u => u.id !== owner?.id)
            .filter(u => Number(u.TurnoReal) !== 3)
            .filter(u => normalize(u.Nombre).includes(normalize(searchTerm.trim())))
            .sort((a, b) => {
                const userDataA = allTasks.find(item => item.user.id === a.id);
                const totalA = userDataA ? userDataA.tasks.reduce((sum, t) => sum + (t.Minutos || 0), 0) : 0;

                const userDataB = allTasks.find(item => item.user.id === b.id);
                const totalB = userDataB ? userDataB.tasks.reduce((sum, t) => sum + (t.Minutos || 0), 0) : 0;

                if (totalA === totalB) return a.Nombre.localeCompare(b.Nombre);
                return totalA - totalB;
            });
    }, [allUsers, allTasks, owner?.id, searchTerm]);

    const handleSelectUserForReassign = (user) => {
        setSelectedReassignUserId(prev => (prev === user.id ? null : user.id));
    };

    const handleFinalReassign = async () => {
        if (!selectedReassignUserId || isSubmitting) return;

        setIsSubmitting(true);

        try {
            const taskDate =
                task?.Fecha ||
                task?.date ||
                selectedDate ||
                moment().format("YYYY-MM-DD");

            const payload = {
                taskId: task.id,
                Empleado: selectedReassignUserId,
                admin: Number(empleado),
                ...(task?.is_projection ? { is_projection: true, date: taskDate } : {}),
            };

            console.log("🔁 REASSIGN FINAL POST →", payload);

            const result = await reassignTask(payload);

            if (result?.OK === 1) {
                onSuccess?.("Tarea reasignada correctamente");
            } else {
                onSuccess?.("Error al reasignar la tarea");
            }

        } catch (error) {
            console.error("❌ Error al reasignar la tarea:", error);
            onSuccess?.("Error al reasignar la tarea");
        } finally {
            onClose?.();
        }
    };

    if (!isOpen) return null;

    const isButtonDisabled = selectedReassignUserId === null || isSubmitting;

    return (
        <div className={styles.reassignModal} onClick={onClose}>
            <div
                className={`${styles.reassignModalContent} ${isMobile ? styles.reassignModalContentMobile : ""}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={isMobile ? styles.modalTitleRowMobile : styles.modalTitleRow}>
                    <h2 className={styles.modalTitle}>Reasignar tarea</h2>

                    <button
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        aria-label="Cerrar"
                    >
                        <img src={CloseIcon} alt="Cerrar" className={styles.modalCloseIcon} />
                    </button>
                </div>

                <p>
                    De:{" "}
                    <span>{owner?.Nombre || "Desconocido"}</span>
                </p>

                <div className={styles.modalHeader}>
                    <div className={styles.modalTaskSummary}>
                        <div
                            className={styles.modalLeftBar}
                            style={{ backgroundColor: task.color || "#ccc" }}
                        />
                        <div className={styles.modalTaskDetails}>
                            <p className={styles.taskTitle} style={{ margin: 0 }}>
                                <span className={styles.task}>{task.Tarea}</span>{" "}
                                <span className={styles.subtask}>{task.Subtarea}</span> para{" "}
                                <span className={styles.project}>{task.Proyecto}</span>
                            </p>

                            <p className={styles.taskTime} style={{ margin: 0 }}>
                                {durationText} {startTime && `| ${timeRange}`}
                            </p>

                            {task.Descripcion?.trim() && !isMobile && (
                                <p className={styles.taskDescription} style={{ margin: 0 }}>
                                    {task.Descripcion}
                                </p>
                            )}

                            {task.Notas?.trim() && (
                                <p className={styles.taskNote} style={{ color: "#00ADC6", margin: 0 }}>
                                    {task.Notas}
                                </p>
                            )}
                        </div>
                    </div>

                    {!isMobile && (
                        <div className={styles.dividerContainer}>
                            <span className={styles.line}></span>
                            <div className={styles.circle}>
                                <img src={ReassignModalIcon} alt="Reassign Icon" className={styles.circleIcon} />
                            </div>
                            <span className={styles.line}></span>
                        </div>
                    )}

                    <div className={`${styles.modalParaContainer} ${isMobile ? styles.modalParaContainerColumn : ""}`}>
                        <p className={styles.modalText}>
                            Para:{" "}
                            <span>
                                {selectedReassignUserId
                                    ? allUsers.find(u => u.id === selectedReassignUserId)?.Nombre
                                    : "Sin asignar"}
                            </span>
                        </p>

                        <div className={`${styles.searchInputWrapper} ${isMobile ? styles.searchInputWrapperMobile : ""}`}>
                            <img
                                src={SearchIcon}
                                alt="Buscar"
                                className={`${styles.searchIcon} ${isMobile ? styles.searchIconMobile : ""}`}
                            />

                            <input
                                type="text"
                                className={`${styles.searchInput} ${isMobile ? styles.searchInputMobile : ""}`}
                                placeholder="Buscar usuario..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />

                            {searchTerm && (
                                <button
                                    type="button"
                                    className={styles.clearButton}
                                    onClick={() => setSearchTerm("")}
                                >
                                    <img src={CloseIcon} alt="Limpiar búsqueda" className={styles.clearIcon} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <OverlayScrollbarsComponent
                    options={{
                        scrollbars: { autoHide: "scroll", autoHideDelay: 300 },
                    }}
                    style={{ maxHeight: isMobile ? "70dvh" : "56vh", width: "100%" }}
                >
                    <div className={styles.userListContainer}>
                        {filteredSortedUsers.map((u) => {
                            const userData = allTasks.find(item => item.user.id === u.id);
                            const userTasks = userData ? userData.tasks : [];
                            const isSelected = u.id === selectedReassignUserId;

                            return (
                                <UserCard
                                    key={u.id}
                                    user={u}
                                    tasks={userTasks}
                                    selectedDate={selectedDate}
                                    onSelectUser={handleSelectUserForReassign}
                                    isSelected={isSelected}
                                    admin={true}
                                    modal={true}
                                    isMobile={isMobile}
                                    isModal={true}
                                    isReassignModal={true}
                                />
                            );
                        })}
                    </div>
                </OverlayScrollbarsComponent>

                <div className={styles.footerButtons}>
                    <button
                        className={isButtonDisabled ? styles.reassignButtonDisabled : styles.reassignButton}
                        disabled={isButtonDisabled}
                        onClick={handleFinalReassign}
                    >
                        {isSubmitting ? "Reasignando..." : "Reasignar tarea"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReassignModal;
